import { useEffect, useMemo, useState } from 'react'
import {
  fetchMovieDetails,
  fetchMovieCast,
  fetchMovieCrew,
  fetchMovieRecommendations,
  fetchTmdbSimilarMovies,
  fetchCollectionDetails,
  type TmdbMovieDetails,
  type TmdbCastMember,
  type TmdbCrewMember,
  type TmdbSimilarTitle,
  type TmdbCollectionDetails,
  type TmdbCollectionPart,
} from '../tmdb/tmdb'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'
import { useLibraryMovies } from '../library/useLibrary'
import { useWriteLibrary } from '../library/useWriteLibrary'
import { useAwardsIndex } from '../awards/useAwards'
import { awardsForMovie, type MovieAwardEntry } from '../awards/awards'
import { WatchlistPicker } from '../watchlists/WatchlistPicker'
import { LazyImage, useToast } from '../ui'
import { useBackButtonClose } from '../hooks/useBackButtonClose'

interface Props {
  tmdbId: number | null
  onClose: () => void
  onPlay: (tmdbId: number, type: 'movie' | 'tv') => void
  onPersonOpen: (personId: number) => void
  /**
   * Fires when the user clicks a citation in the compact Awards section.
   * LibraryView uses this to close the modal, switch to the Awards tab
   * and pre-select the matching body + ceremony.
   */
  onJumpToAwards?: (bodyWikidataId: string, editionId: string) => void
}

/**
 * Full-screen movie detail view — PWA port of the desktop
 * `MovieDetailView`. Matches it section-for-section:
 *
 *   1. Backdrop hero + poster, title, year, runtime, rating, cert, genres
 *   2. Tagline / synopsis
 *   3. User rating (drives library membership) + watchlist / watch
 *   4. Cast row (horizontal scroll, click -> PersonModal)
 *   5. Crew row (director/writers/producers/DoP/composer)
 *   6. Awards grid (from local awards.json)
 *   7. Collection row (if movie belongs to a collection)
 *   8. Recommended movies row
 *   9. Similar movies row
 *  10. External links (TMDB / IMDb)
 *
 * Every section always renders — when data is loading we show a
 * skeleton, when empty we show a "No X" message. The user complained
 * about sections vanishing, so fidelity > terseness.
 *
 * When the user clicks a similar / recommended / collection title, we
 * replace the modal's currentTmdbId state in place (same-tab nav) rather
 * than closing/reopening — mirrors desktop's `showMovieDetail` flow.
 */
export function MovieDetailModal({
  tmdbId,
  onClose,
  onPlay,
  onPersonOpen,
  onJumpToAwards,
}: Props) {
  // Internal navigation stack so that clicking similar/recommended/collection
  // movies swaps the detail view in place (just like the desktop app).
  const [currentTmdbId, setCurrentTmdbId] = useState<number | null>(tmdbId)
  useEffect(() => {
    setCurrentTmdbId(tmdbId)
  }, [tmdbId])

  const { settings } = useUserSettings()
  // `libraryLoading` matters here: until the Firestore snapshot resolves
  // we MUST NOT treat "no entry found" as "movie isn't in library", or
  // the rating buttons fall into the add-to-library branch and overwrite
  // a real synced rating with a fresh empty record.
  const { entries: libraryMovies, loading: libraryLoading } = useLibraryMovies()
  const { addMovieToLibrary, updateMovie } = useWriteLibrary()
  const { index: awardsIndex } = useAwardsIndex()
  const { showSuccess, showError, showInfo } = useToast()

  const [details, setDetails] = useState<TmdbMovieDetails | null>(null)
  const [cast, setCast] = useState<TmdbCastMember[]>([])
  const [crew, setCrew] = useState<TmdbCrewMember[]>([])
  const [recommendations, setRecommendations] = useState<TmdbSimilarTitle[]>([])
  const [similar, setSimilar] = useState<TmdbSimilarTitle[]>([])
  const [collection, setCollection] = useState<TmdbCollectionDetails | null>(null)

  const [loadingDetails, setLoadingDetails] = useState(false)
  const [loadingCast, setLoadingCast] = useState(false)
  const [loadingCrew, setLoadingCrew] = useState(false)
  const [loadingRecs, setLoadingRecs] = useState(false)
  const [loadingSimilar, setLoadingSimilar] = useState(false)
  const [loadingCollection, setLoadingCollection] = useState(false)

  const [writeBusy, setWriteBusy] = useState(false)
  const [watchlistOpen, setWatchlistOpen] = useState(false)
  const [overviewExpanded, setOverviewExpanded] = useState(false)

  // Back button closes the inner Watchlist picker before the detail
  // modal itself. The detail modal's own back-button entry is
  // managed by the library shell in LibraryView.
  useBackButtonClose(watchlistOpen, () => setWatchlistOpen(false))

  const libraryEntry = useMemo(
    () =>
      currentTmdbId == null
        ? undefined
        : libraryMovies.find((m) => m.tmdbId === currentTmdbId),
    [libraryMovies, currentTmdbId]
  )
  const inLibrary = !!libraryEntry
  // Defensive coercion mirrors MovieCard: tolerate a string-shaped rating
  // surfaced from a Firestore doc rather than silently rendering 0/10.
  const rawUserRating = libraryEntry?.record.userRating
  const userRating = (() => {
    if (rawUserRating == null) return 0
    const num =
      typeof rawUserRating === 'number' ? rawUserRating : Number(rawUserRating)
    return Number.isFinite(num) && num > 0 ? num : 0
  })()

  const awards = useMemo<MovieAwardEntry[]>(() => {
    if (!awardsIndex || currentTmdbId == null) return []
    return awardsForMovie(awardsIndex, currentTmdbId)
  }, [awardsIndex, currentTmdbId])

  // Library membership is driven by rating: rating > 0 adds to library,
  // clearing leaves the entry with userRating=null. No separate "remove"
  // action per user preference.
  //
  // We refuse to write while the library subscription is still loading —
  // otherwise we'd treat "snapshot not in yet" as "movie not in library"
  // and clobber the user's existing synced rating with a fresh add.
  const handleSetRating = async (rating: number) => {
    if (currentTmdbId == null) return
    if (libraryLoading) {
      showInfo('Loading library — try again in a second')
      return
    }
    setWriteBusy(true)
    try {
      if (!inLibrary) await addMovieToLibrary(currentTmdbId)
      await updateMovie(currentTmdbId, { userRating: rating })
      showSuccess(`Rated ${rating}/10`)
    } catch (err) {
      console.error('[MovieDetailModal] set rating failed', err)
      showError('Failed to save rating')
    } finally {
      setWriteBusy(false)
    }
  }

  const handleClearRating = async () => {
    if (currentTmdbId == null) return
    if (libraryLoading) {
      showInfo('Loading library — try again in a second')
      return
    }
    setWriteBusy(true)
    try {
      await updateMovie(currentTmdbId, { userRating: null })
      showInfo('Rating cleared')
    } catch (err) {
      console.error('[MovieDetailModal] clear rating failed', err)
      showError('Failed to clear rating')
    } finally {
      setWriteBusy(false)
    }
  }

  // Fetch everything whenever the current movie id changes.
  useEffect(() => {
    if (currentTmdbId == null) {
      setDetails(null)
      setCast([])
      setCrew([])
      setRecommendations([])
      setSimilar([])
      setCollection(null)
      return
    }
    // Falls back to the bundled developer key when the user hasn't supplied one.
    const apiKey = getEffectiveTmdbApiKey(settings)
    if (!apiKey) return
    const language = settings?.tmdbPreferredLanguage || 'en'

    let cancelled = false

    setDetails(null)
    setCast([])
    setCrew([])
    setRecommendations([])
    setSimilar([])
    setCollection(null)
    setOverviewExpanded(false)

    setLoadingDetails(true)
    setLoadingCast(true)
    setLoadingCrew(true)
    setLoadingRecs(true)
    setLoadingSimilar(true)

    fetchMovieDetails(currentTmdbId, apiKey, language).then((d) => {
      if (cancelled) return
      setDetails(d)
      setLoadingDetails(false)
      // Kick off collection fetch once we know the collection id.
      if (d?.collection) {
        setLoadingCollection(true)
        fetchCollectionDetails(d.collection.id, apiKey, language).then((c) => {
          if (cancelled) return
          setCollection(c)
          setLoadingCollection(false)
        })
      } else {
        setCollection(null)
        setLoadingCollection(false)
      }
    })

    fetchMovieCast(currentTmdbId, apiKey, language).then((c) => {
      if (cancelled) return
      setCast(c)
      setLoadingCast(false)
    })
    fetchMovieCrew(currentTmdbId, apiKey, language).then((c) => {
      if (cancelled) return
      setCrew(c)
      setLoadingCrew(false)
    })
    fetchMovieRecommendations(currentTmdbId, apiKey, language).then((r) => {
      if (cancelled) return
      setRecommendations(r)
      setLoadingRecs(false)
    })
    fetchTmdbSimilarMovies(currentTmdbId, apiKey, language).then((s) => {
      if (cancelled) return
      setSimilar(s)
      setLoadingSimilar(false)
    })

    // Reset scroll to top of modal when switching movies.
    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => {
        const host = document.getElementById('movie-detail-modal-scroll')
        if (host) host.scrollTop = 0
      })
    }

    return () => {
      cancelled = true
    }
  }, [currentTmdbId, settings?.tmdbApiKey, settings?.tmdbPreferredLanguage])

  // Lock body scroll + Escape to close.
  useEffect(() => {
    if (tmdbId == null) return
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [tmdbId, onClose])

  if (tmdbId == null || currentTmdbId == null) return null

  // Select notable crew: prefer directors, writers, producers, DoP,
  // composer — in that order, capped at 12 so the section stays tidy.
  const notableCrew = selectNotableCrew(crew)

  // Helper for in-modal navigation.
  const navigateToMovie = (nextId: number) => {
    if (nextId === currentTmdbId) return
    setCurrentTmdbId(nextId)
  }

  return (
    <div
      id="movie-detail-modal-scroll"
      className="fixed inset-0 z-40 overflow-y-auto bg-bg-primary font-inter"
    >
      <div className="relative">
        {/* Backdrop hero */}
        <div className="relative h-56 w-full sm:h-72 md:h-[32rem]">
          {details?.backdropUrl ? (
            <LazyImage
              src={details.backdropUrl}
              alt=""
              className="h-full w-full"
              placeholderColor="#1a1a1a"
            />
          ) : loadingDetails ? (
            <div className="h-full w-full animate-pulse bg-bg-secondary" />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-bg-secondary to-bg-tertiary" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-bg-primary/60 to-bg-primary/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-bg-primary/60 via-transparent to-transparent" />
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="fixed right-4 top-4 z-10 rounded-full bg-bg-secondary/80 p-2 text-text-primary shadow-cinematic backdrop-blur transition-colors hover:bg-bg-tertiary"
        >
          <CloseIcon />
        </button>

        {/* Content wrapper pulled up over the backdrop */}
        <div className="relative mx-auto -mt-28 w-full max-w-screen-xl px-4 pb-16 sm:-mt-32 md:-mt-40 2xl:max-w-screen-2xl">
          {/* Hero: poster + title block */}
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end">
            <div className="shrink-0">
              {details?.posterUrl ? (
                <LazyImage
                  src={details.posterUrl}
                  alt={details.title}
                  wrapperClassName="h-56 w-36 rounded-lg border border-border-primary shadow-cinematic-lg sm:h-72 sm:w-48 md:h-80 md:w-56"
                  placeholderColor="#1a1a1a"
                />
              ) : (
                <div className="h-56 w-36 rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary sm:h-72 sm:w-48 md:h-80 md:w-56" />
              )}
            </div>

            <div className="min-w-0 flex-1 pt-2">
              <h1
                className="font-bold tracking-tight text-text-primary drop-shadow-xl"
                style={{ fontSize: 'clamp(1.5rem, 2.2vw + 1rem, 3rem)', lineHeight: 1.1 }}
              >
                {details?.title || (loadingDetails ? 'Loading…' : `TMDB #${currentTmdbId}`)}
              </h1>
              {details?.originalTitle && details.originalTitle !== details.title && (
                <p className="mt-1 text-sm italic text-text-tertiary">
                  {details.originalTitle}
                </p>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm">
                {details?.year != null && (
                  <span className="font-semibold text-accent-gold">{details.year}</span>
                )}
                {details?.runtime ? (
                  <span className="text-text-secondary">{formatRuntime(details.runtime)}</span>
                ) : null}
                {details && details.rating > 0 && (
                  <span className="flex items-center gap-1 rounded bg-blue-500/80 px-1.5 py-0.5 font-semibold text-white backdrop-blur-sm">
                    ★ {details.rating.toFixed(1)}
                    {details.voteCount > 0 && (
                      <span className="ml-1 text-[10px] font-normal text-white/80">
                        ({formatVoteCount(details.voteCount)})
                      </span>
                    )}
                  </span>
                )}
                {details?.certification && (
                  <span className="rounded border border-border-primary px-1.5 py-0.5 font-semibold uppercase text-text-primary">
                    {details.certification}
                  </span>
                )}
                {userRating > 0 && (
                  <span className="flex items-center gap-1 rounded bg-accent-gold/80 px-1.5 py-0.5 font-semibold text-black">
                    ★ {userRating}/10
                  </span>
                )}
              </div>

              {/* Genres */}
              {details && details.genres.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {details.genres.map((g) => (
                    <span
                      key={g}
                      className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium text-text-primary backdrop-blur-sm"
                    >
                      {g}
                    </span>
                  ))}
                </div>
              )}

              {details?.tagline && (
                <p className="mt-3 text-sm italic text-text-secondary">
                  “{details.tagline}”
                </p>
              )}

              {/* Actions */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onPlay(currentTmdbId, 'movie')}
                  className="inline-flex items-center gap-2 rounded-md bg-accent-primary px-5 py-2.5 text-sm font-semibold text-black shadow-cinematic transition-colors hover:bg-accent-hover"
                >
                  <PlayIcon /> Watch
                </button>
                {details && (
                  <button
                    type="button"
                    onClick={() => setWatchlistOpen(true)}
                    className="inline-flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-bg-tertiary"
                  >
                    ☆ Watchlist
                  </button>
                )}
              </div>

              <RatingStars
                value={userRating}
                onChange={handleSetRating}
                onClear={handleClearRating}
                disabled={writeBusy || libraryLoading}
              />
              {details && (
                <p className="mt-1 text-[11px] text-text-tertiary">
                  {libraryLoading
                    ? 'Loading your rating…'
                    : inLibrary
                      ? '✓ In library'
                      : 'Tap a star to add to your library'}
                </p>
              )}
            </div>
          </div>

          {/* Synopsis */}
          <Section title="Synopsis">
            {loadingDetails && !details && <SkeletonLines lines={4} />}
            {!loadingDetails && !details?.overview && (
              <p className="text-xs text-text-tertiary">No synopsis from TMDB.</p>
            )}
            {details?.overview && (
              <div className="max-w-3xl">
                <p
                  className={`text-sm leading-relaxed text-text-secondary ${
                    overviewExpanded || details.overview.length <= 400 ? '' : 'line-clamp-6'
                  }`}
                >
                  {details.overview}
                </p>
                {details.overview.length > 400 && (
                  <button
                    type="button"
                    onClick={() => setOverviewExpanded((v) => !v)}
                    className="mt-1 text-[11px] font-semibold text-accent-primary hover:text-accent-hover"
                  >
                    {overviewExpanded ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            )}
          </Section>

          {/* Cast */}
          <Section title="Cast">
            {loadingCast && cast.length === 0 && <CastSkeleton />}
            {!loadingCast && cast.length === 0 && (
              <p className="text-xs text-text-tertiary">No cast.</p>
            )}
            {cast.length > 0 && (
              <div className="-mx-4 overflow-x-auto scrollbar-hide">
                <ul className="flex gap-3 px-4 pb-1">
                  {cast.map((c) => (
                    <li key={c.tmdbId} className="w-24 shrink-0 sm:w-28">
                      <button
                        type="button"
                        onClick={() => onPersonOpen(c.tmdbId)}
                        className="group block w-full text-left"
                      >
                        {c.profileUrl ? (
                          <LazyImage
                            src={c.profileUrl}
                            alt={c.name}
                            wrapperClassName="aspect-[3/4] w-full rounded-lg border border-border-primary shadow-cinematic transition-transform duration-300 ease-cinematic group-hover:scale-[1.03]"
                            placeholderColor="#1a1a1a"
                            fallback={
                              <div className="grid aspect-[3/4] w-full place-items-center rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary text-[10px] text-text-tertiary">
                                No image
                              </div>
                            }
                          />
                        ) : (
                          <div className="grid aspect-[3/4] w-full place-items-center rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary text-[10px] text-text-tertiary">
                            No image
                          </div>
                        )}
                        <p className="mt-1.5 line-clamp-1 text-xs font-semibold text-text-primary">
                          {c.name}
                        </p>
                        {c.character && (
                          <p className="line-clamp-1 text-[10px] text-text-tertiary">
                            {c.character}
                          </p>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          {/* Crew */}
          <Section title="Crew">
            {loadingCrew && crew.length === 0 && <CastSkeleton />}
            {!loadingCrew && notableCrew.length === 0 && (
              <p className="text-xs text-text-tertiary">No crew.</p>
            )}
            {notableCrew.length > 0 && (
              <div className="-mx-4 overflow-x-auto scrollbar-hide">
                <ul className="flex gap-3 px-4 pb-1">
                  {notableCrew.map((c) => (
                    <li key={c.creditId} className="w-24 shrink-0 sm:w-28">
                      <button
                        type="button"
                        onClick={() => onPersonOpen(c.tmdbId)}
                        className="group block w-full text-left"
                      >
                        {c.profileUrl ? (
                          <LazyImage
                            src={c.profileUrl}
                            alt={c.name}
                            wrapperClassName="aspect-[3/4] w-full rounded-lg border border-border-primary shadow-cinematic transition-transform duration-300 ease-cinematic group-hover:scale-[1.03]"
                            placeholderColor="#1a1a1a"
                            fallback={
                              <div className="grid aspect-[3/4] w-full place-items-center rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary text-[10px] text-text-tertiary">
                                {c.name
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')
                                  .slice(0, 2)}
                              </div>
                            }
                          />
                        ) : (
                          <div className="grid aspect-[3/4] w-full place-items-center rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary text-[10px] text-text-tertiary">
                            {c.name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                              .slice(0, 2)}
                          </div>
                        )}
                        <p className="mt-1.5 line-clamp-1 text-xs font-semibold text-text-primary">
                          {c.name}
                        </p>
                        <p className="line-clamp-1 text-[10px] text-text-tertiary">{c.job}</p>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          {/* Awards — compact list with deep-link to the Awards tab. */}
          <CompactAwardsSection
            awards={awards}
            loading={!awardsIndex}
            onJumpToAwards={onJumpToAwards}
          />


          {/* Collection — only wraps its own skeleton/empty state; */}
          {/* the section itself always renders when the movie has a collection. */}
          {(details?.collection || loadingDetails) && (
            <Section
              title={
                details?.collection ? `Part of ${details.collection.name}` : 'Collection'
              }
            >
              {(loadingCollection || loadingDetails) && !collection && (
                <GridSkeleton count={6} aspect="2/3" />
              )}
              {!loadingCollection && collection && collection.parts.length > 0 && (
                <div className="-mx-4 overflow-x-auto scrollbar-hide">
                  <ul className="flex gap-3 px-4 pb-1">
                    {collection.parts.map((p) => (
                      <li key={p.tmdbId} className="w-28 shrink-0 sm:w-32">
                        <CollectionTile
                          part={p}
                          current={p.tmdbId === currentTmdbId}
                          onOpen={navigateToMovie}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!loadingCollection && collection && collection.parts.length === 0 && (
                <p className="text-xs text-text-tertiary">No entries in this collection.</p>
              )}
            </Section>
          )}

          {/* Recommendations */}
          <Section title="Recommendations">
            {loadingRecs && recommendations.length === 0 && (
              <GridSkeleton count={6} aspect="2/3" />
            )}
            {!loadingRecs && recommendations.length === 0 && (
              <p className="text-xs text-text-tertiary">No recommendations from TMDB.</p>
            )}
            {recommendations.length > 0 && (
              <div className="-mx-4 overflow-x-auto scrollbar-hide">
                <ul className="flex gap-3 px-4 pb-1">
                  {recommendations.map((m) => (
                    <li key={m.tmdbId} className="w-28 shrink-0 sm:w-32 md:w-36">
                      <SimilarTile title={m} onNavigate={navigateToMovie} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          {/* Similar movies */}
          <Section title="Similar movies">
            {loadingSimilar && similar.length === 0 && (
              <GridSkeleton count={6} aspect="2/3" />
            )}
            {!loadingSimilar && similar.length === 0 && (
              <p className="text-xs text-text-tertiary">No similar movies from TMDB.</p>
            )}
            {similar.length > 0 && (
              <div className="-mx-4 overflow-x-auto scrollbar-hide">
                <ul className="flex gap-3 px-4 pb-1">
                  {similar.map((m) => (
                    <li key={m.tmdbId} className="w-28 shrink-0 sm:w-32 md:w-36">
                      <SimilarTile title={m} onNavigate={navigateToMovie} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          {/* External links */}
          {(details?.tmdbId || details?.imdbId) && (
            <Section title="External">
              <div className="flex flex-wrap gap-2">
                <a
                  href={`https://www.themoviedb.org/movie/${currentTmdbId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-500"
                >
                  TMDB ↗
                </a>
                {details?.imdbId && (
                  <a
                    href={`https://www.imdb.com/title/${details.imdbId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-md bg-yellow-500 px-3 py-1.5 text-xs font-semibold text-black transition-colors hover:bg-yellow-400"
                  >
                    IMDb ↗
                  </a>
                )}
              </div>
            </Section>
          )}

          {details && (
            <WatchlistPicker
              open={watchlistOpen}
              onClose={() => setWatchlistOpen(false)}
              movie={{
                tmdbId: currentTmdbId,
                title: details.title,
                year: details.year,
                posterPath: tmdbRelativePath(details.posterUrl),
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ========================================================================
// Subcomponents
// ========================================================================

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 text-base font-semibold tracking-tight text-text-primary">
        {title}
      </h2>
      {children}
    </section>
  )
}

/**
 * Compact "Awards" section the user wanted instead of the old full
 * table: a one-line aggregate header (`3 wins · 8 nominations`) and a
 * vertical stack of clickable citations. Clicking a citation fires the
 * optional `onJumpToAwards` callback which LibraryView uses to close
 * the modal, switch to the Awards tab and pre-select the matching
 * ceremony.
 *
 * Entries arrive already sorted (wins first, then year desc) from
 * `awardsForMovie`, so we render the list as-is.
 */
function CompactAwardsSection({
  awards,
  loading,
  onJumpToAwards,
}: {
  awards: MovieAwardEntry[]
  loading: boolean
  onJumpToAwards?: (bodyWikidataId: string, editionId: string) => void
}) {
  const wins = awards.filter((a) => a.isWinner).length
  const noms = awards.length - wins
  const summary = useMemo(() => {
    if (awards.length === 0) return null
    const parts: string[] = []
    if (wins > 0) parts.push(`${wins} ${wins === 1 ? 'win' : 'wins'}`)
    if (noms > 0) parts.push(`${noms} ${noms === 1 ? 'nomination' : 'nominations'}`)
    return parts.join(' · ')
  }, [awards.length, wins, noms])

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-baseline gap-3">
        <h2 className="text-base font-semibold tracking-tight text-text-primary">
          Awards
        </h2>
        {summary && (
          <span className="text-xs font-medium text-text-tertiary">
            {summary}
          </span>
        )}
      </div>

      {loading && (
        <p className="text-xs text-text-tertiary">Loading awards…</p>
      )}

      {!loading && awards.length === 0 && (
        <p className="text-xs text-text-tertiary">
          No award nominations on file for this movie.
        </p>
      )}

      {awards.length > 0 && (
        <ul className="space-y-1">
          {awards.map((a, i) => {
            const clickable = !!onJumpToAwards
            const prefix = a.isWinner ? '🏆' : 'Nominee ·'
            const prefixClass = a.isWinner
              ? 'text-accent-gold'
              : 'text-text-tertiary'
            return (
              <li key={`${a.bodyWikidataId}:${a.editionId}:${a.categoryName}:${i}`}>
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={() =>
                    onJumpToAwards?.(a.bodyWikidataId, a.editionId)
                  }
                  className={`w-full rounded px-2 py-1 text-left text-xs leading-relaxed transition-colors ${
                    clickable
                      ? 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
                      : 'cursor-default text-text-secondary'
                  }`}
                >
                  <span className={`mr-1.5 font-semibold ${prefixClass}`}>
                    {prefix}
                  </span>
                  <span className="font-medium text-text-primary">
                    {a.bodyName}
                  </span>
                  <span className="text-text-tertiary"> · </span>
                  <span>{a.categoryName}</span>
                  <span className="text-text-tertiary"> · </span>
                  <span className="tabular-nums text-text-tertiary">
                    {a.year}
                  </span>
                  {a.personName && (
                    <span className="text-text-tertiary">
                      {' '}
                      ({a.personName})
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function SkeletonLines({ lines }: { lines: number }) {
  return (
    <div className="max-w-3xl space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 animate-pulse rounded bg-bg-secondary"
          style={{ width: `${90 - i * 8}%` }}
        />
      ))}
    </div>
  )
}

function CastSkeleton() {
  return (
    <div className="-mx-4 overflow-hidden">
      <div className="flex gap-3 px-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="w-24 shrink-0 sm:w-28">
            <div className="aspect-[3/4] w-full animate-pulse rounded-lg bg-bg-secondary" />
            <div className="mt-1.5 h-3 w-4/5 animate-pulse rounded bg-bg-secondary" />
            <div className="mt-1 h-2 w-2/3 animate-pulse rounded bg-bg-secondary" />
          </div>
        ))}
      </div>
    </div>
  )
}

function GridSkeleton({ count, aspect }: { count: number; aspect: '2/3' | '3/4' }) {
  const aspectClass = aspect === '2/3' ? 'aspect-[2/3]' : 'aspect-[3/4]'
  return (
    <div className="-mx-4 overflow-hidden">
      <div className="flex gap-3 px-4">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="w-28 shrink-0 sm:w-32 md:w-36">
            <div className={`${aspectClass} w-full animate-pulse rounded-lg bg-bg-secondary`} />
            <div className="mt-1.5 h-3 w-4/5 animate-pulse rounded bg-bg-secondary" />
          </div>
        ))}
      </div>
    </div>
  )
}

function SimilarTile({
  title,
  onNavigate,
}: {
  title: TmdbSimilarTitle
  onNavigate: (tmdbId: number) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(title.tmdbId)}
      className="group flex w-full flex-col text-left"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-transform duration-300 ease-cinematic group-hover:scale-[1.03]">
        {title.posterUrl ? (
          <LazyImage
            src={title.posterUrl}
            alt={title.title}
            className="h-full w-full"
            placeholderColor="#1a1a1a"
            fallback={
              <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
                {title.title}
              </div>
            }
          />
        ) : (
          <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
            {title.title}
          </div>
        )}
        {title.rating > 0 && (
          <div className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-blue-500/80 px-1.5 py-0.5 text-[9px] font-semibold text-white backdrop-blur-sm">
            ★ {title.rating.toFixed(1)}
          </div>
        )}
      </div>
      <p className="mt-1.5 line-clamp-2 text-xs font-medium text-text-primary">{title.title}</p>
      {title.year != null && (
        <p className="text-[10px] font-medium text-accent-gold">{title.year}</p>
      )}
    </button>
  )
}

function CollectionTile({
  part,
  current,
  onOpen,
}: {
  part: TmdbCollectionPart
  current: boolean
  onOpen: (tmdbId: number) => void
}) {
  return (
    <button
      type="button"
      onClick={() => !current && onOpen(part.tmdbId)}
      className={`group flex w-full flex-col text-left ${current ? 'cursor-default' : ''}`}
    >
      <div
        className={`relative aspect-[2/3] w-full overflow-hidden rounded-lg border bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-transform duration-300 ease-cinematic ${
          current
            ? 'border-accent-primary ring-2 ring-accent-primary/70'
            : 'border-border-primary group-hover:scale-[1.03]'
        }`}
      >
        {part.posterUrl ? (
          <LazyImage
            src={part.posterUrl}
            alt={part.title}
            className="h-full w-full"
            placeholderColor="#1a1a1a"
            fallback={
              <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
                {part.title}
              </div>
            }
          />
        ) : (
          <div className="grid h-full w-full place-items-center px-2 text-center text-[10px] text-text-tertiary">
            {part.title}
          </div>
        )}
        {current && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <span className="rounded bg-accent-primary px-2 py-0.5 text-[10px] font-bold uppercase text-black">
              Current
            </span>
          </div>
        )}
        {part.rating > 0 && !current && (
          <div className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-blue-500/80 px-1.5 py-0.5 text-[9px] font-semibold text-white backdrop-blur-sm">
            ★ {part.rating.toFixed(1)}
          </div>
        )}
      </div>
      <p className="mt-1.5 line-clamp-2 text-xs font-medium text-text-primary">{part.title}</p>
      {part.year != null && (
        <p className="text-[10px] font-medium text-accent-gold">{part.year}</p>
      )}
    </button>
  )
}

function RatingStars({
  value,
  onChange,
  onClear,
  disabled,
}: {
  value: number
  onChange: (v: number) => void
  onClear?: () => void
  disabled: boolean
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1">
      <span className="mr-2 text-[11px] uppercase tracking-wider text-text-tertiary">
        Your rating
      </span>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
        const active = n <= value
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n === value ? 0 : n)}
            className={`text-xl leading-none transition-colors ${
              active ? 'text-accent-gold' : 'text-text-tertiary hover:text-text-secondary'
            } disabled:opacity-50`}
            aria-label={`Rate ${n}/10`}
          >
            ★
          </button>
        )
      })}
      {value > 0 && (
        <>
          <span className="ml-2 text-xs font-medium text-accent-gold">{value}/10</span>
          {onClear && (
            <button
              type="button"
              disabled={disabled}
              onClick={onClear}
              className="ml-2 text-[11px] text-text-tertiary hover:text-text-primary disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </>
      )}
    </div>
  )
}

// ========================================================================
// Helpers
// ========================================================================

/**
 * Walk the full TMDB crew list and return the "notable" subset the
 * desktop detail view surfaces: directors, writers, producers, DoP and
 * composer, deduped by credit. Order matters — the UI renders whatever
 * we return in-order, with no secondary grouping.
 */
function selectNotableCrew(crew: TmdbCrewMember[]): TmdbCrewMember[] {
  if (!crew.length) return []
  const wantJobs = new Set([
    'Director',
    'Screenplay',
    'Writer',
    'Story',
    'Producer',
    'Executive Producer',
    'Director of Photography',
    'Original Music Composer',
    'Music',
    'Editor',
  ])
  const seen = new Set<string>()
  const ordered: TmdbCrewMember[] = []
  // Preserve the preferred ordering by walking the job allow-list.
  for (const job of wantJobs) {
    for (const c of crew) {
      if (c.job === job && !seen.has(c.creditId)) {
        seen.add(c.creditId)
        ordered.push(c)
      }
    }
  }
  return ordered.slice(0, 12)
}

function tmdbRelativePath(url: string | null | undefined): string | null {
  if (!url) return null
  const m = url.match(/\/t\/p\/w\d+(\/.+)$/)
  return m ? m[1] : null
}

function formatRuntime(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function formatVoteCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}k`
  return String(count)
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8 5v14l11-7z" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}
